import { createContext, ReactNode, useEffect } from 'react';
import { useTranslations } from '../i18n';
import { isRightToLeftLocale } from '../i18n/right-to-left';
import { useLibraryInfo } from '../states/library-info';
import { useUserInfo } from '../states/user-info';

type LoadingState = 'loaded' | 'loading' | 'not-started';

interface MessagesContextProps {
    loadingState: LoadingState;
}

const MessagesContext = createContext<MessagesContextProps>({
    loadingState: 'not-started',
});

interface MessagesProviderProps {
    children: ReactNode;
}

export const MessagesProvider = ({
    children,
}: MessagesProviderProps): JSX.Element => {
    const userInfo = useUserInfo();
    const libraryInfo = useLibraryInfo();
    const translationsLoaded = useTranslations(
        !userInfo.isDefault,
        userInfo.locale,
        libraryInfo.tenant
    );
    const loadingState = translationsLoaded ? 'loaded' : 'loading';

    useEffect(() => {
        const htmlRootElement = document.getElementsByTagName('html')[0];
        htmlRootElement.setAttribute('lang', userInfo.locale);
        htmlRootElement.setAttribute(
            'dir',
            isRightToLeftLocale(userInfo.locale) ? 'rtl' : 'ltr'
        );
    }, [userInfo.locale]);

    return (
        <MessagesContext.Provider value={{ loadingState }}>
            {loadingState === 'loaded' && children}
        </MessagesContext.Provider>
    );
};
